import { useEffect, useRef, useState } from "react";
import { Building2, Cable, CircleDollarSign, Gauge, Zap } from "lucide-react";

const OXAGON_MILESTONES = [
  {
    label: "Nebius contemplated allocation",
    value: "Up to 50 MW",
    proportion: "50 of 100 MW",
    width: "50%",
    color: "bg-[#00a6a6]",
    note: "Proposed framework. This allocation is part of the initial build, not additional capacity.",
  },
  {
    label: "Initial capacity under construction",
    value: "100 MW",
    proportion: "100 of 360 MW",
    width: "27.8%",
    color: "bg-[#3c8ac8]",
    note: "The first build reported by NEOM and Data Center Dynamics. It is not yet operational capacity.",
  },
  {
    label: "Planned first phase",
    value: "360 MW",
    proportion: "360 of 1,500 MW",
    width: "24%",
    color: "bg-[#6855c7]",
    note: "A planned first phase, not a commissioned data-center inventory.",
  },
  {
    label: "Longer-range campus target",
    value: "Up to 1.5 GW",
    proportion: "Reported campus ambition",
    width: "100%",
    color: "bg-[#d99a34]",
    note: "A longer-term target reported by Data Center Dynamics, not capacity available today.",
  },
];

const NVIDIA_PLAN = [
  { value: "14 MW", label: "Blackwell HGX B300", timing: "Q4 2026 target", width: "16.67%", color: "bg-[#2785a5]" },
  { value: "50 MW", label: "Vera Rubin for a US customer", timing: "June 2027 target", width: "59.52%", color: "bg-[#594bb8]" },
  { value: "20 MW", label: "Vera Rubin for HUMAIN Cloud", timing: "July or August 2027 target", width: "23.81%", color: "bg-[#1c9875]" },
];

const CONVERSION_STAGES = [
  {
    icon: Zap,
    title: "Power secured",
    question: "Is firm electricity contracted and deliverable when the workload needs it?",
  },
  {
    icon: Building2,
    title: "Infrastructure delivered",
    question: "Are cooling, networking and compute clusters commissioned and usable?",
  },
  {
    icon: Cable,
    title: "Demand contracted",
    question: "Is there an enforceable commercial commitment rather than an expression of interest?",
  },
  {
    icon: Gauge,
    title: "Compute consumed",
    question: "How much usable capacity is sold and delivered at an acceptable quality of service?",
  },
  {
    icon: CircleDollarSign,
    title: "Value retained",
    question: "After power, depreciation, finance and operations, does durable value remain?",
  },
];

export default function SaudiAiGigaCampusInfographic() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeStage, setActiveStage] = useState<number | null>(null);

  useEffect(() => {
    const target = sectionRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.16 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="saudi-ai-compute-infographic-heading"
      className="not-prose container mx-auto mb-12 max-w-4xl px-4"
    >
      <div className="overflow-hidden rounded-2xl border border-slate-700/70 bg-[#0d1f2d] text-white shadow-[0_18px_70px_rgba(7,22,35,0.22)]">
        <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(0,212,170,0.18),transparent_38%),linear-gradient(120deg,#102b3e,#17253a)] px-5 py-7 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5ee6cb]">Evidence-led infrastructure view</p>
          <h2 id="saudi-ai-compute-infographic-heading" className="mt-2 max-w-3xl font-serif text-2xl leading-tight text-white sm:text-3xl">
            From announced megawatts to commercially useful compute
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-200 sm:text-base">
            These charts separate construction, proposed allocations and longer-term targets. They do not show commissioned capacity, contracted revenue or operating profitability.
          </p>
        </div>

        <div className="space-y-8 p-5 sm:p-8">
          <figure aria-labelledby="oxagon-milestones-heading">
            <figcaption id="oxagon-milestones-heading" className="mb-4">
              <span className="text-sm font-semibold text-[#5ee6cb]">1. Oxagon capacity milestones</span>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                Each bar has its own stated denominator. Bar length is illustrative within that denominator, not a comparison of current operating capacity.
              </p>
            </figcaption>
            <div className="space-y-4 rounded-xl border border-white/10 bg-slate-950/25 p-4 sm:p-5">
              {OXAGON_MILESTONES.map((milestone, index) => (
                <div key={milestone.label}>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">{milestone.label}</p>
                      <p className="text-xs text-slate-400">{milestone.proportion}</p>
                    </div>
                    <p className="font-serif text-lg text-[#ffbf5b]">{milestone.value}</p>
                  </div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-700/80" aria-hidden="true">
                    <div
                      className={`${milestone.color} h-full rounded-full transition-[width] duration-1000 ease-out motion-reduce:transition-none`}
                      style={{ width: isVisible ? milestone.width : "0%", transitionDelay: `${index * 120}ms` }}
                    />
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{milestone.note}</p>
                </div>
              ))}
            </div>
          </figure>

          <figure aria-labelledby="nvidia-rollout-heading">
            <figcaption id="nvidia-rollout-heading" className="mb-4">
              <span className="text-sm font-semibold text-[#5ee6cb]">2. Separate NVIDIA collaboration: 84 MW planned rollout</span>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                The 14 MW, 50 MW and 20 MW segments describe a separate announced programme. Public reporting does not establish whether it overlaps with Oxagon capacity.
              </p>
            </figcaption>
            <div className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/25 p-4 sm:p-5">
              <div className="flex h-12 overflow-hidden rounded-lg bg-slate-700/70" role="img" aria-label="An 84 megawatt planned NVIDIA rollout: 14 megawatts of Blackwell HGX B300, 50 megawatts of Vera Rubin for an unnamed US customer, and 20 megawatts of Vera Rubin for HUMAIN Cloud.">
                {NVIDIA_PLAN.map((phase, index) => (
                  <div
                    key={phase.value}
                    className={`${phase.color} flex items-center justify-center overflow-hidden text-xs font-bold text-white transition-[width] duration-1000 ease-out motion-reduce:transition-none sm:text-sm`}
                    style={{ width: isVisible ? phase.width : "0%", transitionDelay: `${index * 180}ms` }}
                    title={`${phase.value}: ${phase.label}`}
                  >
                    <span className="whitespace-nowrap px-1.5">{phase.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {NVIDIA_PLAN.map((phase) => (
                  <div key={phase.value} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                    <p className="font-serif text-lg text-[#ffbf5b]">{phase.value}</p>
                    <p className="mt-0.5 text-xs font-medium leading-snug text-slate-100">{phase.label}</p>
                    <p className="mt-1 text-xs text-slate-400">{phase.timing}</p>
                  </div>
                ))}
              </div>
            </div>
          </figure>

          <section aria-labelledby="conversion-gap-heading">
            <div className="mb-4">
              <h3 id="conversion-gap-heading" className="text-sm font-semibold text-[#5ee6cb]">
                3. Modi’s POV: the Compute-to-Value Conversion Gap
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                An original Integrated.Social framework for testing whether planned capacity becomes a service customers use and a business that retains value.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {CONVERSION_STAGES.map((stage, index) => {
                const Icon = stage.icon;
                const isActive = activeStage === index;
                return (
                  <button
                    type="button"
                    key={stage.title}
                    onFocus={() => setActiveStage(index)}
                    onBlur={() => setActiveStage(null)}
                    onMouseEnter={() => setActiveStage(index)}
                    onMouseLeave={() => setActiveStage(null)}
                    aria-pressed={isActive}
                    className={`min-h-[166px] rounded-xl border p-4 text-left transition-all duration-200 motion-reduce:transition-none ${
                      isActive
                        ? "border-[#5ee6cb] bg-[#145267] shadow-[0_0_0_1px_rgba(94,230,203,0.16)]"
                        : "border-white/10 bg-white/[0.04] hover:border-white/30 hover:bg-white/[0.08]"
                    }`}
                  >
                    <Icon aria-hidden="true" className="h-5 w-5 text-[#ffbf5b]" />
                    <p className="mt-4 text-sm font-semibold leading-snug text-white">{stage.title}</p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-300">{stage.question}</p>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-400">
              This editorial framework is not a HUMAIN, DataVolt, Nebius or NVIDIA metric. It is a checklist for separating a capacity announcement from demonstrated operating and commercial outcomes.
            </p>
          </section>
        </div>
      </div>
    </section>
  );
}
